import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "../../App";
import { AI_CONCEPTUAL_DISCLOSURE, AI_FLOORPLAN_DISCLOSURE, SCOPE_CONFIRMATION_NOTE } from "./proposalDisclosures";
import { Printer, Download, AlertTriangle, Check } from "lucide-react";
import { normalizeVideos, describeVideos, videoCount } from "./videoLineup";
import { resolvePaymentPlan, pifOffer, pifCopy, pifPctOf, quoteMilestonePolicy, quoteFinalPaymentPolicy, finalDueSentence } from "./paymentPlan";
import { proposalDiscountLabel } from "./proposalDiscount";
import { normalizeFreeAddOns, freeAddOnLineCopy } from "./guardrails";
import { conceptualDesignFeeLabel, conceptualDesignFeeSub, INTERIOR_DESIGN_SCOPE } from "./conceptualDesignFee";
import { MODELING_FEE_LABEL, modelingFeeSub } from "./modelingFee";
import { VR_CONFIGURATOR_LABEL } from "./vrConfigurator";
import {
  BRANDING_SHORT_DESC,
  BRANDING_PROMISE,
  BRANDING_LEDE,
  BRANDING_STAGES,
  BRANDING_DELIVERABLES,
  BRANDING_PROCESS,
  BRANDING_DURATION_NOTE,
  BRANDING_INCLUSIONS,
} from "./brandingPackage";
import { postTermMonthlyOf } from "./vexosClientPricing";

const fmt = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const ACCENT = "#8FA79B";
const CYAN = "#3D6355";

// Orbit structure — mirrors vexosClientPricing.js. The base orbit ships with
// four keyframes; every orbit beyond it is a SUBORBIT (the product formerly
// called a "Master Orbit"), which bundles two keyframes because an orbit built
// on a single keyframe isn't buildable.
const BASE_ORBIT_KEYFRAMES = 4;
const SUBORBIT_INCLUDED_KEYFRAMES = 2;

const kfWord = (n) => ["zero", "one", "two", "three", "four", "five", "six"][n] ?? String(n);

// A Master Plan quote's first N suborbits are in the package, already at
// `masterPlanSubOrbitKeyframes` keyframes. Only what's past that is billed.
const masterPlanIncluded = (c) => Number(c.masterPlanIncludedSubOrbits) || 0;
const masterPlanIncludedExtraKf = (c) =>
  Math.max(0, (Number(c.masterPlanSubOrbitKeyframes) || SUBORBIT_INCLUDED_KEYFRAMES) - SUBORBIT_INCLUDED_KEYFRAMES);

/** "3 on the base orbit · 2 on suborbit 1" — says which orbit the extras ride on. */
function additionalKeyframeScope(c) {
  const total = Number(c.additionalKfQty) || 0;
  const subOrbits = Array.isArray(c.subOrbits) ? c.subOrbits.map((n) => Number(n) || 0) : null;
  const base = c.baseOrbitExtraKfQty != null ? Number(c.baseOrbitExtraKfQty) || 0 : total;
  if (total <= 0) return undefined;
  const parts = [];
  if (base > 0) parts.push(`${base} on the base orbit`);
  if (subOrbits) {
    subOrbits.forEach((extra, i) => {
      const billed = i < masterPlanIncluded(c) ? Math.max(0, extra - masterPlanIncludedExtraKf(c)) : extra;
      if (billed > 0) parts.push(`${billed} on suborbit ${i + 1}`);
    });
  }
  return parts.length ? parts.join(" · ") : `${total} additional keyframe${total === 1 ? "" : "s"}`;
}

/** "2 suborbits · two keyframes each". */
function suborbitScope(c) {
  const qty = Math.max(0, (Number(c.masterOrbitsQty) || 0) - masterPlanIncluded(c));
  if (qty <= 0) return undefined;
  return `${qty} suborbit${qty === 1 ? "" : "s"} · ${SUBORBIT_INCLUDED_KEYFRAMES === 2 ? "two" : SUBORBIT_INCLUDED_KEYFRAMES} keyframes each`;
}

/**
 * "2 unit types · 4 levels" when any 3D unit is multi-level, plain "N plans"
 * otherwise. A bare count can't say whether "3" is three unit types or one
 * tri-level unit; quotes saved before levels existed fall through to the
 * plain form.
 */
function floorPlan3DScope(c) {
  const plans = Number(c.total3DPlans) || 0;
  const units = Number(c.total3DUnits) || 0;
  const levels = Number(c.total3DLevels) || 0;
  if (units > 0 && levels > units) {
    return `${units} unit type${units === 1 ? "" : "s"} · ${levels} levels`;
  }
  return `${plans} plan${plans === 1 ? "" : "s"}`;
}

export default function ProposalPage() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    apiClient.get(`/vexos-quotes/${id}`)
      .then((r) => { if (alive) setQuote(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Failed to load proposal."); });
    return () => { alive = false; };
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#000000] text-white flex items-center justify-center px-8">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-amber-300/80" />
          <p className="text-[18px] mb-2">{err}</p>
          <p className="text-[12px] text-white/40">Proposal ID: {id}</p>
        </div>
      </div>
    );
  }
  if (!quote) {
    return (
      <div className="min-h-screen bg-[#000000] text-white flex items-center justify-center">
        <p className="text-[13px] text-white/40">Loading proposal\u2026</p>
      </div>
    );
  }

  const c = quote.calc_snapshot || {};
  const ps = quote.payment_schedule || {};
  // Hand-priced quote with discounts removed: the calculator suspended every
  // discount before saving, so the proposal must not reference one. The
  // savings rows below already fall away on their own amounts; this covers the
  // copy that always prints. A rep can keep the discounts with overrides in
  // place, so the saved flag wins; only quotes that predate it fall back to the
  // override map (back then any override removed the discounts).
  const manualPricing = "manualPricing" in c
    ? !!c.manualPricing
    : !!(c.priceOverrides && Object.keys(c.priceOverrides).length);
  // Three project types: condo | sfh | community. Anything unrecognized
  // (including legacy quotes with no project_type) falls back to community.
  const projectType = (quote.project_type || "").trim().toLowerCase();
  const isCondo = projectType === "condo";
  const isSFH = projectType === "sfh";
  const inputs = quote.inputs || {};
  // A Master Plan is a community quote with the expanded orbit package.
  const isMasterPlan = !isCondo && !isSFH && !!(quote.master_plan || c.masterPlan || inputs.c_masterPlan);
  // Complimentary add-ons the rep gave ([{ id, label, value, qty }]).
  const freeAddOns = normalizeFreeAddOns(inputs.freeAddOns);
  const createdDate = new Date(quote.created_at).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
  const validThrough = new Date(new Date(quote.created_at).getTime() + 30 * 24 * 60 * 60 * 1000)
    .toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  // Respect the VEXOS master toggle — same logic as the sidebar and backend PDF generator.
  const vexosEnabled = c.vexosEnabled !== undefined ? !!c.vexosEnabled : (inputs.vexosEnabled !== undefined ? !!inputs.vexosEnabled : !!inputs.vexosActive);

  // Detect Lot Explorer quote — mirrors backend detect_proposal_mode logic.
  const isLotExplorerQuote = !!(
    c.lotExplorer?.enabled ||
    inputs.lotExplorer?.enabled ||
    inputs.activeTab === "lotexplorer" ||
    c.activeTab === "lotexplorer"
  );

  if (isLotExplorerQuote) {
    return (
      <LotExplorerProposal
        quote={quote}
        createdDate={createdDate}
        validThrough={validThrough}
        id={id}
      />
    );
  }

  // Derive primary count + plan count for display (condo/community only).
  const primaryCount = isCondo ? (Number(inputs.units) || 0) : (Number(inputs.c_lots) || 0);
  const planCount = isCondo ? (Number(inputs.uniqueFloorPlans) || 0) : (Number(inputs.c_homeModels) || 0);
  const amenityCount = isCondo ? (Number(inputs.amenitySpaces) || 0) : (Number(inputs.c_amenitySpaces) || 0);
  // A single-family home is one residence — describe it by size/floors.
  const sfhHomeSqFt = Number(inputs.sfh_homeSqFt) || 0;
  const sfhFloors = Number(inputs.sfh_floors) || 0;
  const sfhAmenitySqFt = Number(inputs.sfh_amenitySqFt) || 0;
  // Condo → "Floor plan", SFH → "Interior", community → "Home model".
  const projectTypeLabel = isCondo ? "Condo Building" : isSFH ? "Single-Family Home" : isMasterPlan ? "Master Plan" : "Community";
  const interiorSubhead = (isCondo || isSFH) ? "Interior Experience" : "Home Model Experience";
  const interiorNoun = isCondo ? "Floor plan" : isSFH ? "Interior" : "Home model";

  // Marketing & Collateral (Tab 2) — folded into scope, investment, and
  // payment schedule below using the same math as the calculator sidebar
  // and the combined-mode backend PDF (`_page_combined_investment_summary`),
  // so all three surfaces always agree on the numbers.
  const m = quote.marketing || {};
  const col = quote.collateral || {};
  const marketingActive = !!m.active;
  const collateralActive = !!col.active;
  const marketTimeline = Number(m.sellOutMonths) || 0;
  const marketSetup = Number(m.setupFee) || 0;
  const marketMgmt = Number(m.monthlyServiceFee) || 0;
  const marketAd = Number(m.monthlyAdSpend) || 0;
  const marketLeads = Number(m.monthlyLeads) || 0;
  const colTotal = Number(col.collateralTotal) || 0;
  const colRetainer = Number(col.retainerMonthly) || 0;

  // (key, label, rate-card fallback, description). The price actually shown
  // comes from `col.prices` — the rate-card snapshot the calculator freezes
  // onto the quote at save time — so an admin price change never leaves these
  // lines disagreeing with the collateral total they sum into. The fallbacks
  // only cover quotes saved before that snapshot existed.
  const COLLATERAL_ITEMS = [
    ["landingPage", "Landing Page", 3000, "Single high-converting page for lead capture and campaign traffic."],
    ["fullWebsite", "Full Website", 7500, "Multi-page website with full structure and user journey."],
    ["brandingPackage", "Branding & Conceptual Package", 10000, BRANDING_SHORT_DESC],
    ["investmentPresentation", "Investment Presentation", 4000, "Investor-facing deck (up to 20 pages)."],
    ["developmentFlipbook", "Development Flipbook", 7500, "Comprehensive project sales/marketing book (40–50 pages)."],
    ["billboardStatic", "Billboard Design (Static)", 1500, "Billboard-ready static creative using project visuals."],
    ["billboardAnimated", "Billboard Design (Animated)", 3000, "Billboard-ready animated creative up to 10 seconds."],
    ["unitInventorySheet", "Unit Inventory Breakdown Sheet", 750, "Clean, structured unit matrix for sales teams and buyers."],
  ];
  const colPrice = (key, fallback) => {
    const saved = Number(col.prices?.[key]);
    return Number.isFinite(saved) && saved >= 0 ? saved : fallback;
  };
  // The branding package is the one collateral line with its own section
  // below — point the client at it rather than leaving a $10k lump sum.
  const brandingActive = collateralActive && !!col.brandingPackage;
  const collateralLines = collateralActive
    ? COLLATERAL_ITEMS.filter(([key]) => col[key]).map(([key, label, px, desc]) => ({
        label,
        value: fmt(colPrice(key, px)),
        // A collateral line for branding can only exist when the branding
        // section below exists, so the pointer is unconditional.
        desc: key === "brandingPackage"
          ? `${desc} Itemized in full in section 03 below.`
          : desc,
      }))
    : [];
  if (collateralActive && Number(col.animatedRendersQty) > 0) {
    collateralLines.push({
      label: `Animated Renders · ${col.animatedRendersQty}`,
      value: fmt(Number(col.animatedRendersQty) * colPrice("animatedRendersPer", 899)),
      desc: "Short-form animated clips from existing renderings for ads and social media.",
    });
  }
  if (collateralActive && Number(col.renderEnhancementQty) > 0) {
    collateralLines.push({
      label: `Render Enhancements · ${col.renderEnhancementQty}`,
      value: fmt(Number(col.renderEnhancementQty) * colPrice("renderEnhancementPer", 599)),
      desc: "AI-enhanced improvements to existing renderings for realism and visual impact.",
    });
  }
  // Section numbering shifts when the branding spread is present.
  const SEC = brandingActive
    ? { overview: "01", scope: "02", branding: "03", investment: "04", payment: "05", terms: "06" }
    : { overview: "01", scope: "02", investment: "03", payment: "04", terms: "05" };

  // À La Carte (visualization-only) — NO VEXOS platform setup fee or monthly
  // subscription. Mirrors the backend PDF's alacarte handling so the web
  // proposal and PDF agree. Older quotes persisted platform fields on the
  // snapshot, so strip them here at render time too (fixes existing quotes
  // without a re-save). Revisions ride the viz split; travel bills at signing.
  const isAlacarte = !!(c.isAlacarte || inputs.isAlacarte || inputs.activeTab === "alacarte" || c.activeTab === "alacarte");
  const showVexosPlatform = vexosEnabled && !isAlacarte;
  // After-month-12 maintenance fee: the rep's figure, else $750 ($250 SFH).
  // This is the "Maintenance Fee stated in the Proposal & Agreement" that the
  // VEXOS PSA §3(D) and MSA §3.7 bind the client to.
  const postTermMonthly = postTermMonthlyOf(quote);
  // The rep's additional proposal discount is its own line; it comes out of
  // the visualization split so the milestones sum to the discounted total
  // (oneTimeAllIn already has it taken off). See ./proposalDiscount.
  const proposalDiscount = Number(c.proposalDiscount) || 0;
  // The flat conceptual design fee rides the same split, added (oneTimeAllIn
  // already carries it). See ./conceptualDesignFee.
  const conceptualDesignFee = Number(c.conceptualDesignFee) || 0;
  // The 3D modeling fee rides the same split. See ./modelingFee.
  const modelingFee = Number(c.modelingFee) || 0;
  const acVizSplit = Math.max(0, (Number(c.vizNet) || 0) + (Number(c.revisionsCost) || 0) - proposalDiscount + conceptualDesignFee + modelingFee);
  const acTravel = Number(c.travelCost) || 0;
  // The plan, the amounts, and the Payment 2 trigger are recomputed from the
  // snapshot by the shared resolver rather than read off the saved schedule, so
  // this page, the PDF, and the Stripe deposit always agree — and so a quote
  // saved before a plan change picks up the current policy with no re-save.
  const plan = resolvePaymentPlan({
    setupFee: Number(c.setupFee) || 0,
    vizSubtotal: acVizSplit,
    travelCost: acTravel,
    // Collateral rides the plan percentages alongside the viz scope, so the
    // resolver owns it — the milestones below must not add it a second time.
    collateralTotal: collateralActive ? colTotal : 0,
    isAlacarte,
    alacartePayPlan: inputs.alacartePayPlan,
    // Single-payment terms, when the rep wrote them into the quote.
    payPlanChoice: inputs.payPlanChoice,
    scope: c,
    pifDiscount: inputs.pifDiscount,
    modelingFee,
    // …with ONE exception to that recompute: the V1 milestone's wording is
    // pinned to the quote, because re-wording a proposal a client already
    // holds is not a policy pickup, it is a change of terms.
    milestonePolicy: quoteMilestonePolicy(quote),
    finalPaymentPolicy: quoteFinalPaymentPolicy(quote),
  });
  // The pay-in-full discount the rep set on this quote: 5%, or 0 when they
  // switched it off. Every pifOffer() / pifCopy() call on this page has to
  // carry it — the offer is rebuilt over wider pots of one-time money than the
  // plan divides, and each rebuild would otherwise restore the discount.
  const pifPct = pifPctOf(inputs.pifDiscount);
  // Platform + total figures with à la carte stripped.
  const effSetupFee   = showVexosPlatform ? (Number(c.setupFee) || 0) : 0;
  const effMonthlyRate= showVexosPlatform ? (Number(c.monthlyRate) || 0) : 0;
  const effRecurring  = showVexosPlatform ? (Number(c.recurringContractValue) || 0) : 0;
  const effOneTime    = isAlacarte ? (acVizSplit + acTravel) : (Number(c.oneTimeAllIn) || 0);

  // Combined payment split — the marketing campaign setup folds into Payment 1
  // (it bills whole at signing), and marketing management + the social retainer
  // fold into the recurring monthly line. Collateral is already inside
  // plan.p1 / p2 / p3 via the plan percentages; `colHalf` is non-zero only for
  // a quote the plan can't divide (no viz, no platform), where collateral keeps
  // its standalone 50 / 50 signing / delivery schedule.
  const baseP1 = plan.p1;
  const baseP2 = plan.p2;
  const baseP3 = plan.p3;
  const baseMonthly = isAlacarte ? 0 : (ps.monthly || 0);
  const colHalf = collateralActive && !plan.collateralInSplit ? colTotal * 0.5 : 0;
  const p1Combined = baseP1 + (marketingActive ? marketSetup : 0) + colHalf;
  const p2Combined = baseP2 + colHalf;
  const monthlyCombined = baseMonthly + (marketingActive ? marketMgmt : 0) + (collateralActive ? colRetainer : 0);
  // What the client is actually billed every month. Zero on a quote with no
  // platform, no management fee and no retainer — a collateral-only sale — and
  // every recurring line below is gated on it.
  const recurringMonthlyDue = (marketingActive || collateralActive) ? monthlyCombined : effMonthlyRate;

  // Pay in full: the alternative to the milestones above, over EVERY one-time
  // dollar the schedule bills (01 + 02 + 03), so the two options always
  // reconcile on the page. Derived from the rows rather than from the totals
  // table, which also carries recurring money. Travel is pass-through and
  // rides the total at cost.
  const oneTimeDue = p1Combined + p2Combined + baseP3;
  // Never offered against a schedule that is already a single payment — the
  // client would be reading two prices for the same thing, the milestone and a
  // box beneath it offering the identical payment for less.
  const pif = plan.singlePayment
    ? { eligible: false }
    : pifOffer({ discountable: oneTimeDue - acTravel, atCost: acTravel, pct: pifPct });
  const pifText = pifCopy({ atCost: acTravel, hasRecurring: recurringMonthlyDue > 0, pct: pifPct });

  // Payment rows are numbered in the order they print, and single-payment terms
  // remove two of them. Numbered off a running counter rather than literals: a
  // schedule that jumps from 01 to 04 reads as two payments already missed.
  // Reset on every render, since this is the component body.
  let payRowNo = 0;
  const nextPayNo = () => String(++payRowNo).padStart(2, "0");

  const oneTimeTotal = effOneTime + (marketingActive ? marketSetup : 0) + (collateralActive ? colTotal : 0);
  const recurringMgmtTotal = marketingActive ? marketMgmt * marketTimeline : 0;
  const recurringRetainerTotal = collateralActive ? colRetainer * (Number(quote.contract_term) || 0) : 0;
  const grandTotalCombined = oneTimeTotal + effRecurring + recurringMgmtTotal + recurringRetainerTotal;
  const grandTotalSingle = effOneTime + effRecurring;

  return (
    <div className="min-h-screen bg-white text-[#000000]" style={{ fontFamily: 'var(--font-sans)' }}>
      {/* Top action bar — hidden in print */}
      <div className="sticky top-0 z-50 bg-white border-b border-black/[0.06] px-8 py-3 flex items-center justify-between no-print">
        <p className="text-[12px] uppercase tracking-[0.2em] text-[#8A8783]">{isAlacarte ? "Visualization Proposal & Agreement" : "VEXOS Proposal & Agreement"}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const res = await apiClient.get(`/vexos-quotes/${id}/proposal-html`, {
                  responseType: "text",
                  transformResponse: [(d) => d],
                });
                const blob = new Blob([res.data], { type: "text/html" });
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank", "noopener,noreferrer");
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              } catch (e) { /* fail silently — print fallback remains */ }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] border border-black/15 text-[#000000] hover:border-black/40 hover:bg-black/[0.03] transition-colors"
            data-testid="download-proposal-pdf-btn"
          >
            <Download className="w-3.5 h-3.5" />
            Download Proposal
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] bg-[#000000] text-white hover:bg-black transition-colors"
            data-testid="print-proposal-btn"
          >
            <Printer className="w-3.5 h-3.5" />
            Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Page content */}
      <article className="max-w-[860px] mx-auto px-10 py-14 print:py-0 print:px-0">

        {/* COVER ─────────────────────────────────────────────────────── */}
        <header className="mb-16 pb-10 border-b border-black/[0.08]">
          <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: ACCENT }}>The Render Pros &middot; {isAlacarte ? "Visualization Studio" : "VEXOS Platform"}</p>
          <h1 className="mt-6 text-[44px] leading-[1.02] tracking-tight" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>
            Proposal &amp; Agreement
          </h1>
          <p className="mt-3 text-[15px] text-[#565350]">
            Prepared for <span className="text-[#000000]">{quote.client_name}</span>
            {quote.client_entity && <> &middot; <span className="text-[#000000]">{quote.client_entity}</span></>}
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6 text-[12px]">
            <Meta label="Date" value={createdDate} />
            <Meta label="Valid Through" value={validThrough} />
            <Meta label="Proposal ID" value={quote.id.slice(0, 8).toUpperCase()} />
          </div>
        </header>

        {/* PROJECT OVERVIEW ─────────────────────────────────────────── */}
        <Section label={`${SEC.overview} &middot; Project Overview`}>
          <div className="grid grid-cols-2 gap-y-3 gap-x-10 text-[13px]">
            <Field label="Project type" value={projectTypeLabel} />
            {showVexosPlatform && <Field label="Contract term" value={`${quote.contract_term}-month agreement`} />}
            {isSFH ? (
              <>
                {sfhHomeSqFt > 0 && <Field label="Home size" value={`${sfhHomeSqFt.toLocaleString()} sq ft`} />}
                {sfhFloors > 0 && <Field label="Floors" value={sfhFloors.toLocaleString()} />}
                {sfhAmenitySqFt > 0 && <Field label="Outdoor amenity area" value={`${sfhAmenitySqFt.toLocaleString()} sq ft`} />}
              </>
            ) : (
              <>
                <Field label={isCondo || isMasterPlan ? "Total units" : "Total lots"} value={primaryCount.toLocaleString()} />
                {/* A lot-sales community can have no home models — never print "0". */}
                {(isCondo || planCount > 0) && (
                  <Field label={isCondo ? "Unique floor plans" : "Home models"} value={planCount.toLocaleString()} />
                )}
                <Field label="Amenity spaces" value={amenityCount.toLocaleString()} />
                {isCondo && Number(inputs.floors) > 0 && (
                  <Field label="Floors" value={Number(inputs.floors).toLocaleString()} />
                )}
              </>
            )}
          </div>
        </Section>

        {/* SCOPE OF SERVICES ────────────────────────────────────────── */}
        <Section label={`${SEC.scope} &middot; Scope of Services`}>
          {showVexosPlatform && <ScopeLine label="VEXOS Platform Setup &mdash; one-time" value={fmt(effSetupFee)} sub="Onboarding, integration, and platform setup" />}
          {showVexosPlatform && effMonthlyRate > 0 && (
          <ScopeLine
            label="VEXOS Platform Fee &mdash; monthly installments"
            value={`${fmt(effMonthlyRate)} / mo`}
            sub={`${fmt(effRecurring)} platform fee, paid over the ${quote.contract_term}-month contract term &middot; ${postTermMonthly > 0 ? `then ${fmt(postTermMonthly)} / mo to keep it live, month to month, cancel anytime` : "then stays live at no monthly charge"}`}
          />
          )}

          {/* Exterior — the orbit base package (and orbit add-ons) is a VEXOS-only
              inclusion. À La Carte exteriors are renderings + VR only, so only show
              the Exterior subhead when there's exterior content to list. */}
          {(!isAlacarte || (c.exteriorRendersQty || 0) > 0) && <SubHeading>Studio Visualization &mdash; Exterior</SubHeading>}
          {!isAlacarte && (
            <>
              <ScopeLine label={isCondo ? "Building orbit base package" : isSFH ? "Home orbit base package" : isMasterPlan ? "Master orbit base package" : "Community orbit base package"} value="Included" sub={`Full orbit &middot; ${BASE_ORBIT_KEYFRAMES} keyframes &middot; live availability overlay`} />
              {masterPlanIncluded(c) > 0 && (
                <ScopeLine
                  label="Suborbits"
                  value="Included"
                  sub={`${masterPlanIncluded(c)} suborbits &middot; ${kfWord(Number(c.masterPlanSubOrbitKeyframes) || SUBORBIT_INCLUDED_KEYFRAMES)} keyframes each`}
                />
              )}
              {(c.additionalKfCost || 0) > 0 && (
                <ScopeLine
                  label="Additional keyframes"
                  value={fmt(c.additionalKfCost)}
                  sub={additionalKeyframeScope(c)}
                />
              )}
              {(c.masterOrbitCost || 0) > 0 && (
                <ScopeLine
                  label="Additional orbit"
                  value={fmt(c.masterOrbitCost)}
                  sub={suborbitScope(c)}
                />
              )}
              {(c.dayToNightCost || 0) > 0 && (
                <ScopeLine
                  label="Day-to-night upgrade"
                  value={fmt(c.dayToNightCost)}
                  sub={(c.dayToNightKfQty || 0) > 0
                    ? `${c.dayToNightKfQty} keyframe${c.dayToNightKfQty === 1 ? "" : "s"} across every orbit`
                    : undefined}
                />
              )}
              {(c.animatedKfCost || 0) > 0 && <ScopeLine label="Animated keyframes" value={fmt(c.animatedKfCost)} />}
            </>
          )}
          {(c.exteriorRendersQty || 0) > 0 && (
            <ScopeLine
              label="Exterior renderings"
              value={fmt(c.exteriorRendersCost)}
              sub={`${c.exteriorRendersQty} render${c.exteriorRendersQty === 1 ? "" : "s"} &middot; combined volume tier with interior`}
            />
          )}
          {modelingFee > 0 && (
            <>
              <SubHeading>3D Modeling</SubHeading>
              <ScopeLine
                label={MODELING_FEE_LABEL}
                value={fmt(modelingFee)}
                sub={escapeHtml(modelingFeeSub(c.modelingFeeTier))}
              />
            </>
          )}
          {/* Conceptual Design — its own scope, not a kind of exterior render. */}
          {((c.conceptualRendersQty || 0) > 0 || conceptualDesignFee > 0) && (
            <>
              <SubHeading>Conceptual Design</SubHeading>
              {(c.conceptualRendersQty || 0) > 0 && (
                <ScopeLine
                  label="Conceptual renderings"
                  value={fmt(c.conceptualRendersCost)}
                  sub={`${c.conceptualRendersQty} image${c.conceptualRendersQty === 1 ? "" : "s"} &middot; ${AI_CONCEPTUAL_DISCLOSURE}`}
                />
              )}
              {conceptualDesignFee > 0 && (
                <ScopeLine
                  label={conceptualDesignFeeLabel(c.conceptualDesignFeeSpaces)}
                  value={fmt(conceptualDesignFee)}
                  sub={escapeHtml(conceptualDesignFeeSub(c.conceptualDesignFeeNote, c.conceptualDesignFeeSpaces, c.conceptualDesignFeeRate))
                    + ((Number(c.conceptualDesignFeeSpaces) || 0) > 0
                      ? `<br><b>Included:</b> ${escapeHtml(INTERIOR_DESIGN_SCOPE.included)}<br><b>Not included:</b> ${escapeHtml(INTERIOR_DESIGN_SCOPE.excluded)}`
                      : "")}
                />
              )}
            </>
          )}

          {(c.totalInteriorRenders > 0 || c.totalInteriorVR > 0) && (
            <>
              <SubHeading>{interiorSubhead}</SubHeading>
              {c.totalInteriorRenders > 0 && (
                <ScopeLine
                  label={`${interiorNoun} renderings`}
                  value={fmt(c.interiorRendersCost)}
                  sub={`${c.totalInteriorRenders} render${c.totalInteriorRenders === 1 ? "" : "s"} total`}
                />
              )}
              {c.totalInteriorVR > 0 && (
                <ScopeLine
                  label={`${interiorNoun} VR tours`}
                  value={fmt(c.interiorVRCost)}
                  sub={vrToursSub(c.totalInteriorVR, c.interiorVRTours, interiorNoun === "Home model" ? "Home model" : "Floor plan", c.interiorVRPointsMin, c.interiorVRPointsMax, "tour")}
                />
              )}
              {(c.residenceConfiguratorCost || 0) > 0 && (
                <ScopeLine
                  label={VR_CONFIGURATOR_LABEL}
                  value={fmt(c.residenceConfiguratorCost)}
                  sub={escapeHtml(c.residenceConfiguratorSub || "")}
                />
              )}
            </>
          )}

          {(c.totalAmenityRenders > 0 || c.totalAmenityVR > 0) && (
            <>
              <SubHeading>Amenity Experience</SubHeading>
              {c.totalAmenityRenders > 0 && (
                <ScopeLine
                  label="Amenity renderings"
                  value={fmt(c.amenityRendersCost)}
                  sub={`${c.totalAmenityRenders} render${c.totalAmenityRenders === 1 ? "" : "s"} total`}
                />
              )}
              {c.totalAmenityVR > 0 && (
                <ScopeLine
                  label="Amenity VR tours"
                  value={fmt(c.amenityVRCost)}
                  sub={vrToursSub(c.totalAmenityVR, c.amenityVRTours, "Amenity space", c.amenityVRPointsMin, c.amenityVRPointsMax, "space")}
                />
              )}
              {(c.amenityConfiguratorCost || 0) > 0 && (
                <ScopeLine
                  label={VR_CONFIGURATOR_LABEL}
                  value={fmt(c.amenityConfiguratorCost)}
                  sub={escapeHtml(c.amenityConfiguratorSub || "")}
                />
              )}
            </>
          )}

          {(c.aiMotionDur != null || c.cinematicDur != null) && (
            <>
              <SubHeading>Video Animation</SubHeading>
              {c.aiMotionDur != null && (
                <ScopeLine
                  label={videoCount(normalizeVideos(c.aiMotionVideos, c.aiMotionDur)) > 1 ? "AI motion videos" : "AI motion video"}
                  value={fmt(c.aiMotionCost)}
                  sub={`${describeVideos(normalizeVideos(c.aiMotionVideos, c.aiMotionDur))}${c.aiMotionClientRenders ? " · animated from client-provided renderings (min. 5–6)" : ""}`}
                />
              )}
              {c.cinematicDur != null && (
                <ScopeLine
                  label={videoCount(normalizeVideos(c.cinematicVideos, c.cinematicDur)) > 1 ? "Cinematic animations" : "Cinematic animation"}
                  value={fmt(c.cinematicCost)}
                  sub={describeVideos(normalizeVideos(c.cinematicVideos, c.cinematicDur))}
                />
              )}
              {c.motionPackageActive && (c.motionPackageSavings || 0) > 0 && (
                <ScopeLine
                  label={`Motion Package &mdash; ${c.motionPackagePct}% off combined video`}
                  value={`\u2212${fmt(c.motionPackageSavings)}`}
                  sub="Automatic discount when both AI Motion + Cinematic are selected"
                  accent
                />
              )}
            </>
          )}

          {((c.total2DPlans || 0) > 0 || (c.total3DPlans || 0) > 0) && (
            <>
              <SubHeading>Floor Plans</SubHeading>
              {(c.total2DPlans || 0) > 0 && (
                <>
                  <ScopeLine
                    label="2D floor plans"
                    value={fmt(c.floorPlans2DPct > 0 ? c.floorPlans2DGross : c.floorPlans2DCost)}
                    sub={`${c.total2DPlans} plan${c.total2DPlans === 1 ? "" : "s"} &middot; CAD vectorized`}
                  />
                  {(c.floorPlans2DPct || 0) > 0 && (
                    <ScopeLine
                      label={`Volume discount &mdash; ${c.floorPlans2DPct}% off 2D`}
                      value={`\u2212${fmt(c.floorPlans2DSavings)}`}
                      sub="Applied automatically at 2D quantity threshold"
                      accent
                    />
                  )}
                </>
              )}
              {(c.total3DPlans || 0) > 0 && (
                <>
                  <ScopeLine
                    label="3D floor plans"
                    value={fmt(c.floorPlans3DPct > 0 ? c.floorPlans3DGross : c.floorPlans3DCost)}
                    sub={`${floorPlan3DScope(c)} &middot; ${
                      c.floorPlans3DWorkflow === "traditional"
                        ? "traditional 3D pipeline &middot; 2 revision rounds &middot; exact to spec"
                        : c.floorPlans3DWorkflow === "hybrid"
                        ? `${AI_FLOORPLAN_DISCLOSURE} &middot; 1 revision round`
                        : "dimensioned perspective"
                    }`}
                  />
                  {(c.floorPlans3DPct || 0) > 0 && (
                    <ScopeLine
                      label={`Volume discount &mdash; ${c.floorPlans3DPct}% off 3D`}
                      value={`\u2212${fmt(c.floorPlans3DSavings)}`}
                      sub="Applied automatically at 3D quantity threshold"
                      accent
                    />
                  )}
                </>
              )}
            </>
          )}

          {(c.revisionRounds || 0) > 0 && (c.revisionsCost || 0) > 0 && (
            <>
              <SubHeading>Additional Revisions</SubHeading>
              <ScopeLine
                label={`Additional revision round${c.revisionRounds === 1 ? "" : "s"}`}
                value={fmt(c.revisionsCost)}
                sub={`${c.revisionRounds} round${c.revisionRounds === 1 ? "" : "s"} beyond the ${c.revisionsIncluded || 0} included &middot; ${c.revisionsPct || 0}% of net visualization scope per round`}
              />
            </>
          )}

          {(c.travelDays || 0) > 0 && (c.travelCost || 0) > 0 && (
            <>
              <SubHeading>On-Site Travel</SubHeading>
              <ScopeLine
                label={`On-site travel &mdash; ${c.travelDays} day${c.travelDays === 1 ? "" : "s"}`}
                value={fmt(c.travelCost)}
                sub={`Our team on-site &middot; ${fmt(c.travelDayRate)} / day`}
              />
            </>
          )}

          {/* Complimentary add-ons — given at no charge. Listed for their
              value, priced "Included"; they are in no total or payment. */}
          {freeAddOns.length > 0 && (
            <>
              <SubHeading>Complimentary</SubHeading>
              {freeAddOns.map((a) => {
                const line = freeAddOnLineCopy(a);
                return (
                  <ScopeLine key={a.id} label={escapeHtml(line.label)} value={line.value} sub={escapeHtml(line.sub)} />
                );
              })}
            </>
          )}

          {c.bundleSavings > 0 && (
            <>
              <SubHeading>Bundle Incentive</SubHeading>
              <ScopeLine
                label={`${c.bundle?.label} &mdash; ${c.bundle?.pct}% off visualization scope`}
                value={`\u2212${fmt(c.bundleSavings)}`}
                sub="Applied automatically to your full-scope commitment"
                accent
              />
            </>
          )}

          {proposalDiscount > 0 && (
            <>
              <SubHeading>Additional Discount</SubHeading>
              <ScopeLine
                label={proposalDiscountLabel(c.proposalDiscountPct)}
                value={`\u2212${fmt(proposalDiscount)}`}
                sub="Applied to your one-time investment"
                accent
              />
            </>
          )}

          {marketingActive && (
            <>
              <SubHeading>Marketing &amp; Lead Generation</SubHeading>
              <ScopeLine
                label="Campaign setup &mdash; one-time"
                value={fmt(marketSetup)}
                sub="CRM &middot; landing page &middot; Meta account setup &middot; pixel &amp; analytics &middot; ad creative &middot; lead system &middot; automated reporting"
              />
              {marketMgmt > 0 && (
                <ScopeLine
                  label="Monthly management fee"
                  value={`${fmt(marketMgmt)} / mo`}
                  sub="Strategy, creative, optimization, weekly check-ins, monthly reporting"
                />
              )}
              {marketAd > 0 && (
                <ScopeLine
                  label="Monthly ad budget"
                  value={`${fmt(marketAd)} / mo *`}
                  sub={marketLeads > 0 && marketTimeline > 0
                    ? `Est. ${Math.round(marketLeads).toLocaleString()} leads/mo over a ${marketTimeline}-month campaign`
                    : "Client-controlled spend, deployed directly to Meta and Google"}
                />
              )}
            </>
          )}

          {collateralActive && collateralLines.length > 0 && (
            <>
              <SubHeading>Collateral &amp; Sales Materials</SubHeading>
              {collateralLines.map((l, i) => (
                <ScopeLine key={i} label={l.label} value={l.value} sub={l.desc} />
              ))}
              {colRetainer > 0 && (
                <ScopeLine
                  label="AI social media retainer"
                  value={`${fmt(colRetainer)} / mo`}
                  sub="Monthly retainer for AI-generated 15-second video clips for ads and social media"
                />
              )}
            </>
          )}
        </Section>

        {/* BRANDING PACKAGE ─────────────────────────────────────────
            The one collateral line that sells as a system rather than an
            artifact, so it gets its own section instead of a price row. */}
        {brandingActive && (
          <Section label={`${SEC.branding} &middot; Branding &amp; Conceptual Package`} pageBreakBefore>
            <p className="text-[16px] text-[#000000] leading-snug">{BRANDING_PROMISE}</p>
            <p className="mt-3 text-[13px] text-[#565350] leading-relaxed max-w-[62ch]">{BRANDING_LEDE}</p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 border border-black/[0.08] rounded-[10px] overflow-hidden">
              {[
                [String(BRANDING_DELIVERABLES.length), "Defined deliverables"],
                [String(BRANDING_STAGES.length), "Stages, start to handoff"],
                [fmt(colPrice("brandingPackage", 10000)), "One-time · all of the below"],
              ].map(([value, label], i, arr) => (
                <div
                  key={label}
                  className="px-4 py-3 border-b md:border-b-0 md:border-r border-black/[0.08] last:border-r-0"
                  style={i === arr.length - 1 ? { background: "rgba(197,162,103,0.08)" } : undefined}
                  data-testid={`proposal-branding-stat-${i}`}
                >
                  <p className="text-[20px] tabular-nums leading-none" style={{ color: i === arr.length - 1 ? ACCENT : "#000000", fontWeight: 450 }}>{value}</p>
                  <p className="mt-1.5 text-[9.5px] uppercase tracking-[0.14em] text-[#8A8783]">{label}</p>
                </div>
              ))}
            </div>

            {BRANDING_STAGES.map((stage) => (
              <div key={stage.id} className="mt-7" data-testid={`proposal-branding-stage-${stage.id}`}>
                <div className="flex items-baseline gap-3 pb-1.5 border-b border-black/[0.08]">
                  <span className="text-[10px] uppercase tracking-[0.2em] whitespace-nowrap" style={{ color: ACCENT }}>{stage.label}</span>
                  <span className="text-[11.5px] text-[#8A8783]">{stage.caption}</span>
                </div>
                <div className={`grid gap-x-6 gap-y-4 pt-4 grid-cols-1 ${stage.deliverables.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                  {stage.deliverables.map((d) => (
                    <div key={d.id} className="flex gap-2.5" data-testid={`proposal-branding-item-${d.id}`}>
                      <span className="text-[10px] tabular-nums pt-[3px] shrink-0" style={{ color: ACCENT }}>
                        {String(BRANDING_DELIVERABLES.findIndex((x) => x.id === d.id) + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0">
                        <p className="text-[13px] text-[#000000]" style={{ fontWeight: 450 }}>{d.title}</p>
                        <p className="mt-1 text-[11px] leading-[1.55] text-[#565350]">{d.blurb}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <SubHeading>How the engagement runs</SubHeading>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {BRANDING_PROCESS.map((st) => (
                <div key={st.step} className="pt-2.5" style={{ borderTop: `2px solid ${ACCENT}` }}>
                  <p className="text-[9.5px] tracking-[0.16em]" style={{ color: ACCENT }}>{st.step}</p>
                  <p className="mt-1 text-[12.5px] text-[#000000]" style={{ fontWeight: 450 }}>{st.title}</p>
                  <p className="mt-1.5 text-[10.5px] leading-[1.5] text-[#565350]">{st.detail}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] leading-[1.6] text-[#8A8783] max-w-[62ch]">{BRANDING_DURATION_NOTE}</p>

            <SubHeading>What is included</SubHeading>
            <ul className="list-disc pl-4 space-y-1.5">
              {BRANDING_INCLUSIONS.map((n, i) => (
                <li key={i} className="text-[11px] leading-[1.6] text-[#565350]">{n}</li>
              ))}
            </ul>
          </Section>
        )}

        {/* INVESTMENT ───────────────────────────────────────────────── */}
        <Section label={`${SEC.investment} &middot; Investment Summary`}>
          <table className="w-full text-[14px]">
            <tbody>
              <tr className="border-b border-black/[0.06]">
                <td className="py-3 text-[#565350]">
                  {(marketingActive || collateralActive) ? "One-time investment — VEXOS & visualization" : "One-time investment"}
                  {proposalDiscount > 0 && (
                    <span className="block text-[11px] text-[#8A8783] mt-0.5">After the {fmt(proposalDiscount)} additional discount</span>
                  )}
                </td>
                <td className="py-3 text-right tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>{fmt(effOneTime)}</td>
              </tr>
              {marketingActive && (
                <tr className="border-b border-black/[0.06]">
                  <td className="py-3 text-[#565350]">Marketing campaign setup &mdash; one-time</td>
                  <td className="py-3 text-right tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>{fmt(marketSetup)}</td>
                </tr>
              )}
              {collateralActive && colTotal > 0 && (
                <tr className="border-b border-black/[0.06]">
                  <td className="py-3 text-[#565350]">Collateral &amp; sales materials &mdash; one-time</td>
                  <td className="py-3 text-right tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>{fmt(colTotal)}</td>
                </tr>
              )}
              {showVexosPlatform && effMonthlyRate > 0 && (
              <tr className="border-b border-black/[0.06]">
                <td className="py-3 text-[#565350]">Recurring platform &mdash; {quote.contract_term} mo &times; {fmt(effMonthlyRate)}</td>
                <td className="py-3 text-right tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>{fmt(effRecurring)}</td>
              </tr>
              )}
              {marketingActive && marketMgmt > 0 && (
                <tr className="border-b border-black/[0.06]">
                  <td className="py-3 text-[#565350]">Recurring marketing management &mdash; {marketTimeline} mo &times; {fmt(marketMgmt)}</td>
                  <td className="py-3 text-right tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>{fmt(recurringMgmtTotal)}</td>
                </tr>
              )}
              {collateralActive && colRetainer > 0 && (
                <tr className="border-b border-black/[0.06]">
                  <td className="py-3 text-[#565350]">AI social media retainer &mdash; {quote.contract_term} mo &times; {fmt(colRetainer)}</td>
                  <td className="py-3 text-right tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>{fmt(colRetainer * (quote.contract_term || 0))}</td>
                </tr>
              )}
              <tr>
                <td className="pt-5 text-[15px]">Total contract value</td>
                <td className="pt-5 text-right tabular-nums text-[26px]" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>
                  {fmt((marketingActive || collateralActive) ? grandTotalCombined : grandTotalSingle)}
                </td>
              </tr>
            </tbody>
          </table>
          {marketingActive && marketAd > 0 && (
            <p className="text-[11px] text-[#8A8783] mt-3">* Client-controlled advertising spend of {fmt(marketAd)} / mo is deployed directly into Meta and Google, is not retained by The Render Pros, and is not included in the totals above.</p>
          )}
        </Section>

        {/* PAYMENT SCHEDULE ─────────────────────────────────────────── */}
        <Section label={`${SEC.payment} &middot; Payment Schedule`}>
          {plan.singlePayment ? (
            /* Single-payment terms: the whole one-time scope at signing, and no
               delivery-gated rows at all. The saving, when the rep left the
               discount on, is stated on the row — there is no alternative
               option underneath for it to sit in. */
            <PaymentRow
              n={nextPayNo()}
              label="Due at signing &mdash; paid in full"
              sub={[
                "the entire one-time scope",
                ...(acTravel > 0 ? ["including on-site travel at cost"] : []),
                ...(marketingActive ? ["marketing campaign setup in full"] : []),
                ...(plan.singlePaymentSavings > 0
                  ? [`${plan.pifPct}% pay-in-full discount applied &mdash; you save ${fmt(plan.singlePaymentSavings)}`]
                  : []),
              ].join(" &middot; ")}
              value={fmt((marketingActive || collateralActive) ? p1Combined : baseP1)}
            />
          ) : (
          <>
          <PaymentRow
            n={nextPayNo()}
            label="Due at signing &mdash; project initiation"
            sub={[
              ...plan.scopeParts.p1,
              ...(plan.setupAtSigning ? ["VEXOS setup fee in full"] : []),
              ...(acTravel > 0 ? ["on-site travel"] : []),
              ...(marketingActive ? ["marketing campaign setup in full"] : []),
              ...(colHalf > 0 ? ["50% of collateral"] : []),
            ].join(" &middot; ")}
            value={fmt((marketingActive || collateralActive) ? p1Combined : baseP1)}
          />
          <PaymentRow
            n={nextPayNo()}
            label={plan.twoPart ? "Due on finalization" : `Due at ${plan.v1.dueLabelLower}`}
            sub={`${[
              ...plan.scopeParts.p2,
              ...(colHalf > 0 ? ["remaining 50% of collateral"] : []),
            ].join(" &middot; ")}${plan.twoPart ? ` &mdash; ${plan.finalAssets}, complete and approved; collected prior to final delivery` : ""}`}
            value={fmt(colHalf > 0 ? p2Combined : baseP2)}
          />
          {!plan.twoPart && (
          <PaymentRow
            n={nextPayNo()}
            label="Due on finalization"
            sub={`${plan.scopeParts.p3.join(" &middot; ")} &mdash; ${plan.finalAssets}, complete and approved; collected prior to final delivery`}
            value={fmt(baseP3)}
          />
          )}
          </>
          )}
          {/* Only where a payment is gated on V1 — a 75 / 25 has none. */}
          {showVexosPlatform && !plan.singlePayment && !plan.twoPart && (
            <div className="mt-3 mb-1 rounded-lg px-4 py-3" style={{ background: "rgba(0,0,0,0.025)", border: "1px solid rgba(0,0,0,0.06)" }}>
              <p className="text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: ACCENT }}>{plan.v1.title}</p>
              <ul className="text-[11px] text-[#565350] leading-relaxed list-disc pl-4 space-y-1">
                {plan.v1.bullets.map((b, i) => <li key={i}>{b}</li>)}
                <li>{finalDueSentence({ pct: plan.pct.p3, assets: plan.finalAssets, policy: plan.finalPaymentPolicy })}</li>
              </ul>
            </div>
          )}
          {/* Pay in full — an ALTERNATIVE to 01 / 02 / 03 above, not a fourth
              payment. Rendered as its own bordered block, and deliberately
              un-numbered, so it can't be read as another amount owed. */}
          <PifOption offer={pif} copy={pifText} />
          {/* A recurring milestone is only a milestone if money recurs. A
              collateral-only or setup-fee-only quote has no monthly at all,
              and "$0 / mo" on a payment schedule reads as a charge the client
              has not agreed to. Same for the ad-spend row. */}
          {recurringMonthlyDue > 0 && (
          <PaymentRow
            n={nextPayNo()}
            label="Monthly installments"
            sub={`Begins post-launch &middot; ${quote.contract_term} monthly installments${marketingActive && marketMgmt > 0 ? " &middot; includes marketing management" : ""}${collateralActive && colRetainer > 0 ? " + AI social media retainer" : ""}`}
            value={`${fmt(recurringMonthlyDue)} / mo`}
          />
          )}
          {marketingActive && marketAd > 0 && (
            <PaymentRow
              n={nextPayNo()}
              label="Client-controlled monthly ad spend"
              sub="Deployed directly to Meta &amp; Google &middot; not retained by The Render Pros"
              value={`~${fmt(marketAd)} / mo *`}
            />
          )}
        </Section>

        {/* AGREEMENT ────────────────────────────────────────────────── */}
        <Section label={`${SEC.terms} &middot; Agreement Terms`} pageBreakBefore>
          <p className="text-[13px] leading-relaxed text-[#302E2B] mb-4">
            This Agreement (&ldquo;Agreement&rdquo;) is entered into between
            {" "}<strong className="text-[#000000]">The Render Pros LLC</strong> (&ldquo;Render Pros&rdquo;) and
            {" "}<strong className="text-[#000000]">{quote.client_entity || quote.client_name}</strong> (&ldquo;Client&rdquo;)
            as of {createdDate}.
          </p>

          <Clause n="1" title="Scope">
            Render Pros will deliver the visualization{showVexosPlatform ? ", VR, and VEXOS platform services" : " and VR services"}{(marketingActive || collateralActive) ? ", together with the marketing, lead-generation, and/or collateral services," : ""} described in Sections 01&ndash;03 of this proposal. Any additions or substitutions outside this scope require a written change order signed by both parties.
          </Clause>

          <Clause n="2" title="Fees &amp; Payment Terms">
            Client shall pay the total contract value of <strong>{fmt((marketingActive || collateralActive) ? grandTotalCombined : grandTotalSingle)}</strong> according to the Payment Schedule in Section 04.{showVexosPlatform && effMonthlyRate > 0 && <> The VEXOS platform fee of <strong>{fmt(effRecurring)}</strong> is a fixed fee for the {quote.contract_term}-month contract term, amortized into monthly installments of <strong>{fmt(effMonthlyRate)} / mo</strong> beginning on the platform launch date. It is a payment plan for the committed fee, not a subscription. {postTermMonthly > 0
              ? <> After the first twelve (12) months, the platform stays live on a month-to-month basis for a Maintenance Fee of <strong>{fmt(postTermMonthly)} / mo</strong>, which Client may cancel at any time by written notice, effective at the end of the month already invoiced.</>
              : <> After the first twelve (12) months, the platform stays live on a month-to-month basis with no Maintenance Fee.</>}</>}
            {marketingActive && marketMgmt > 0 && <> The monthly marketing management fee of <strong>{fmt(marketMgmt)} / mo</strong> runs for the {marketTimeline}-month campaign term.</>}
            {marketingActive && marketAd > 0 && <> Advertising spend of approximately <strong>{fmt(marketAd)} / mo</strong> is controlled by and billed directly to Client via Meta and Google, and is not collected by Render Pros.</>}
            {pif.eligible && <> In place of that schedule, Client may elect to pay the one-time scope in full at signing for <strong>{fmt(pif.total)}</strong>{pif.pct > 0 ? <>, a {pif.pct}% saving{acTravel > 0 ? " on all one-time fees other than on-site travel, which is billed at cost" : ""}</> : null}{recurringMonthlyDue > 0 ? "; recurring fees are unaffected" : ""}.</>}
            {" "}Late payments accrue at 1.5% per month, and a $5,000 reinstatement fee applies from the 14th business day past due. Invoices are due net-15 unless otherwise specified.
          </Clause>

          <Clause n="3" title="Change Orders">
            {SCOPE_CONFIRMATION_NOTE}{" "}Material changes to scope, unit count, plan count, amenity count, or visualization deliverables will be documented via a Change Order and re-priced using the same rate methodology as this proposal.{!manualPricing && " Bundle discounts are recalculated based on the final composite scope."}
          </Clause>

          <Clause n="4" title="Intellectual Property">
            Upon receipt of full payment, Client owns all final deliverables outright (every rendering, animation, video, and other visualization delivered to Client) and may use them for any purpose without further fee or restriction.{isAlacarte ? "" : " Render Pros retains ownership of the VEXOS platform, its proprietary technology, which Client uses for the contract term."} Render Pros may display the deliverables in its portfolio.
          </Clause>

          <Clause n="5" title="Confidentiality">
            Each party shall keep confidential all non-public business, technical, and pricing information disclosed by the other. This obligation survives termination for three (3) years.
          </Clause>

          <Clause n="6" title="Term &amp; Termination">
            This Agreement begins on the date signed and continues through the platform contract term plus the delivery period for one-time services{showVexosPlatform && effMonthlyRate > 0 ? ", and then month to month for as long as Client keeps the platform live on the maintenance fee" : ""}. Either party may terminate for material breach with thirty (30) days written notice if the breach is not cured. Termination by Client after work has commenced does not relieve Client of payment for work performed and authorized through the termination date.
          </Clause>

          <Clause n="7" title="Limitation of Liability">
            Neither party shall be liable for indirect, incidental, or consequential damages. Render Pros&rsquo; aggregate liability shall not exceed the total fees paid by Client under this Agreement in the twelve months preceding the claim.
          </Clause>

          <Clause n="8" title="Governing Law">
            This Agreement is governed by the laws of the State of Florida, without regard to its conflict-of-laws principles. Any dispute shall be resolved in the state or federal courts located in Miami-Dade County, Florida.
          </Clause>

          <Clause n="9" title="Entire Agreement">
            This document, together with any signed change orders, constitutes the entire agreement between the parties and supersedes all prior or contemporaneous proposals, communications, and understandings.
          </Clause>
        </Section>

        {/* SIGNATURE ────────────────────────────────────────────────── */}
        <section className="mt-14 pt-10 border-t border-black/[0.1] page-break-inside-avoid">
          <p className="text-[11px] uppercase tracking-[0.2em] mb-8" style={{ color: ACCENT }}>Acceptance &amp; Signature</p>
          <div className="grid grid-cols-2 gap-12">
            <SignBlock party="Client" name={quote.client_entity || quote.client_name} />
            <SignBlock party="The Render Pros LLC" name="Authorized Signatory" />
          </div>
          <p className="text-[11px] text-[#8A8783] mt-10 text-center">
            This proposal is valid through {validThrough}. By signing above, Client accepts the scope, fees, payment schedule, and agreement terms set forth herein.
          </p>
        </section>
      </article>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, html { background: #ffffff !important; }
          article { padding: 0.5in !important; max-width: 100% !important; }
          @page { size: letter portrait; margin: 0.5in; }
          .page-break-before { page-break-before: always; }
          .page-break-inside-avoid { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 *  LOT EXPLORER PROPOSAL — standalone layout, rendered when
 *  calc_snapshot.lotExplorer.enabled === true. Shows only Lot Explorer
 *  deliverables; no VEXOS platform, visualization, or marketing content.
 * ───────────────────────────────────────────────────────────────────────── */
function LotExplorerProposal({ quote, createdDate, validThrough, id }) {
  const le = quote.calc_snapshot?.lotExplorer || quote.inputs?.lotExplorer || {};

  const lotCount       = Math.max(0, parseInt(le.lotCount) || 0);
  const ratePerLot     = Math.max(0, parseFloat(le.ratePerLot) || 0);
  const baseFee        = le.baseFee != null ? Math.max(0, parseFloat(le.baseFee) || 0) : 7500;
  const lotSubtotal    = lotCount * ratePerLot;
  const grandTotal     = baseFee + lotSubtotal;
  const timelineWeeks  = Math.max(1, parseInt(le.timelineWeeks) || 4);
  const hostingMonthly = Math.max(0, parseFloat(le.hostingMonthly) || 0);

  const deposit       = Math.round(grandTotal * 0.50);
  const v1            = Math.round(grandTotal * 0.25);
  const finalDelivery = Math.max(0, grandTotal - deposit - v1);
  // Pay in full, over the three milestones it replaces. Lot Explorer has no
  // travel line, so the whole platform investment earns the discount; hosting
  // is recurring and stays outside it.
  const lePifPct = pifPctOf(quote.inputs?.pifDiscount);
  const lePif = pifOffer({ discountable: deposit + v1 + finalDelivery, pct: lePifPct });

  const features = [
    { key: "poi",     label: "POI Widget",           sub: "Points of interest overlay — schools, parks, transit, amenities",     active: !!le.includePoi },
    { key: "crm",     label: "CRM Integration",       sub: "Inquiry capture synced to your CRM on lot interaction",               active: !!le.includeCrm },
    { key: "media",   label: "Media Pop-up Support",  sub: "Per-lot photo, video, and virtual tour media panels",                 active: !!le.includeMedia },
    { key: "backend", label: "Backend Management",    sub: "Client-controlled lot status, availability, and pricing editor",     active: !!le.includeBackend },
  ].filter(f => f.active);

  return (
    <div className="min-h-screen bg-white text-[#000000]" style={{ fontFamily: 'var(--font-sans)' }}>
      {/* Action bar — hidden in print */}
      <div className="sticky top-0 z-50 bg-white border-b border-black/[0.06] px-8 py-3 flex items-center justify-between no-print">
        <p className="text-[12px] uppercase tracking-[0.2em] text-[#8A8783]">VEXOS Lot Explorer&trade; &middot; Proposal &amp; Agreement</p>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const res = await apiClient.get(`/vexos-quotes/${id}/proposal-html`, {
                  responseType: "text",
                  transformResponse: [(d) => d],
                });
                const blob = new Blob([res.data], { type: "text/html" });
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank", "noopener,noreferrer");
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              } catch (e) { /* print fallback remains */ }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] border border-black/15 text-[#000000] hover:border-black/40 hover:bg-black/[0.03] transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Download PDF
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] bg-[#000000] text-white hover:bg-black transition-colors"
          >
            <Printer className="w-3.5 h-3.5" />
            Print / Save as PDF
          </button>
        </div>
      </div>

      <article className="max-w-[860px] mx-auto px-10 py-14 print:py-0 print:px-0">

        {/* COVER ──────────────────────────────────────────────────────── */}
        <header className="mb-16 pb-10 border-b border-black/[0.08]">
          <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: CYAN }}>The Render Pros &middot; VEXOS Lot Explorer&trade;</p>
          <h1 className="mt-6 text-[44px] leading-[1.02] tracking-tight" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>
            Proposal &amp; Agreement
          </h1>
          <p className="mt-3 text-[15px] text-[#565350]">
            Prepared for <span className="text-[#000000]">{quote.client_name}</span>
            {quote.client_entity && <> &middot; <span className="text-[#000000]">{quote.client_entity}</span></>}
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6 text-[12px]">
            <Meta label="Date" value={createdDate} />
            <Meta label="Valid Through" value={validThrough} />
            <Meta label="Proposal ID" value={quote.id.slice(0, 8).toUpperCase()} />
          </div>
        </header>

        {/* 01 · PROJECT OVERVIEW ───────────────────────────────────────── */}
        <LESection label="01 &middot; Project Overview">
          <div className="grid grid-cols-2 gap-y-3 gap-x-10 text-[13px]">
            <Field label="Product" value="VEXOS Lot Explorer™" />
            <Field label="Total lots" value={lotCount.toLocaleString()} />
            <Field label="Rate per lot" value={fmt(ratePerLot)} />
            <Field label="Base platform fee" value={fmt(baseFee)} />
            <Field label="Estimated delivery" value={`~${timelineWeeks} weeks`} />
            {hostingMonthly > 0 && (
              <Field label="Recurring hosting &amp; support" value={`${fmt(hostingMonthly)} / mo`} />
            )}
          </div>
        </LESection>

        {/* 02 · PLATFORM FEATURES ──────────────────────────────────────── */}
        {features.length > 0 && (
          <LESection label="02 &middot; Platform Features">
            <div className="space-y-0">
              {features.map(f => (
                <div key={f.key} className="flex items-start gap-3 py-3 border-b border-black/[0.05]">
                  <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: CYAN }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-[#000000] font-medium">{f.label}</p>
                    <p className="text-[11px] text-[#8A8783] mt-0.5">{f.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </LESection>
        )}

        {/* 03 · INVESTMENT SUMMARY ─────────────────────────────────────── */}
        <LESection label={`${features.length > 0 ? "03" : "02"} · Investment Summary`}>
          <table className="w-full text-[14px]">
            <tbody>
              <tr className="border-b border-black/[0.06]">
                <td className="py-3 text-[#565350]">Base platform fee</td>
                <td className="py-3 text-right tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>
                  {fmt(baseFee)}
                </td>
              </tr>
              <tr className="border-b border-black/[0.06]">
                <td className="py-3 text-[#565350]">{lotCount.toLocaleString()} lots &times; {fmt(ratePerLot)} per lot</td>
                <td className="py-3 text-right tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>
                  {fmt(lotSubtotal)}
                </td>
              </tr>
              {hostingMonthly > 0 && (
                <tr className="border-b border-black/[0.06]">
                  <td className="py-3 text-[#565350]">Recurring hosting &amp; support (post-launch)</td>
                  <td className="py-3 text-right tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>
                    {fmt(hostingMonthly)} / mo
                  </td>
                </tr>
              )}
              <tr>
                <td className="pt-5 text-[15px]">Total project investment</td>
                <td className="pt-5 text-right tabular-nums text-[26px]" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>
                  {fmt(grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </LESection>

        {/* 04 · PAYMENT SCHEDULE ────────────────────────────────────────── */}
        <LESection label={`${features.length > 0 ? "04" : "03"} · Payment Schedule`}>
          <PaymentRow
            n="01"
            label="Deposit — Due at signing"
            sub="Initiates project setup, sitemap preparation, and backend configuration."
            value={fmt(deposit)}
          />
          <PaymentRow
            n="02"
            label="V1 Delivery"
            sub="Interactive sitemap deployed &middot; zoom/pan operational &middot; initial lot interaction &middot; initial media pop-up."
            value={fmt(v1)}
          />
          <PaymentRow
            n="03"
            label="Final Delivery"
            sub="Finalized lot data &middot; availability logic &middot; CRM connection &middot; POI implementation &middot; final QA."
            value={fmt(finalDelivery)}
          />
          <PifOption offer={lePif} copy={pifCopy({ hasRecurring: hostingMonthly > 0, pct: lePifPct })} />
          {hostingMonthly > 0 && (
            <PaymentRow
              n="04"
              label="Recurring Hosting &amp; Support"
              sub="Begins post-launch &middot; ongoing platform operations, updates, and dedicated support."
              value={`${fmt(hostingMonthly)} / mo`}
            />
          )}
        </LESection>

        {/* 05 · AGREEMENT TERMS ────────────────────────────────────────── */}
        <LESection label={`${features.length > 0 ? "05" : "04"} · Agreement Terms`} pageBreakBefore>
          <p className="text-[13px] leading-relaxed text-[#302E2B] mb-4">
            This Agreement (&ldquo;Agreement&rdquo;) is entered into between{" "}
            <strong className="text-[#000000]">The Render Pros LLC</strong> (&ldquo;Render Pros&rdquo;) and{" "}
            <strong className="text-[#000000]">{quote.client_entity || quote.client_name}</strong> (&ldquo;Client&rdquo;)
            {" "}as of {createdDate}.
          </p>

          <Clause n="1" title="Scope">
            Render Pros will design, develop, and deliver the VEXOS Lot Explorer&trade; interactive sitemap platform described in Sections 01&ndash;02 of this proposal, including all selected platform features. Pricing consists of a {fmt(baseFee)} base platform fee plus {lotCount.toLocaleString()} lots at {fmt(ratePerLot)} per lot. Any additions to lot count, features, or integrations outside this scope require a written change order signed by both parties.
          </Clause>

          <Clause n="2" title="Fees &amp; Payment Terms">
            Client shall pay the total project investment of <strong>{fmt(grandTotal)}</strong> ({fmt(baseFee)} base platform fee + {lotCount.toLocaleString()} lots &times; {fmt(ratePerLot)}) according to the Payment Schedule in Section {features.length > 0 ? "04" : "03"} (50% deposit at signing, 25% at V1 delivery, 25% at final delivery).
            {hostingMonthly > 0 && <> The recurring hosting and support fee of <strong>{fmt(hostingMonthly)} / mo</strong> begins on the platform launch date and continues on a month-to-month basis unless otherwise agreed in writing.</>}
            {lePif.eligible && <> In place of that schedule, Client may elect to pay the platform investment in full at signing for <strong>{fmt(lePif.total)}</strong>{lePif.pct > 0 ? <>, a {lePif.pct}% saving</> : null}{hostingMonthly > 0 ? "; recurring hosting and support is unaffected" : ""}.</>}
            {" "}Late payments accrue at 1.5% per month, and a $5,000 reinstatement fee applies from the 14th business day past due. Invoices are due net-15 unless otherwise specified.
          </Clause>

          <Clause n="3" title="Deliverables &amp; Acceptance">
            Render Pros will deliver a functional interactive sitemap meeting the specifications defined herein. Client will have five (5) business days following each delivery milestone to review and provide written feedback. Silence beyond that period constitutes acceptance of the delivered milestone.
          </Clause>

          <Clause n="4" title="Change Orders">
            Material changes to lot count, feature set, integrations, or data requirements will be documented via a Change Order and re-priced using the same per-lot rate methodology as this proposal.
          </Clause>

          <Clause n="5" title="Intellectual Property">
            Upon final payment, Client receives a perpetual, royalty-free license to operate the delivered VEXOS Lot Explorer&trade; platform for the named project.
            {hostingMonthly > 0 && " Continued access to hosting infrastructure and platform updates is contingent on the active hosting and support agreement."}
            {" "}Render Pros retains ownership of the platform codebase, tooling, and process IP. Render Pros may use anonymized screenshots for portfolio purposes unless Client provides written objection within 30 days of delivery.
          </Clause>

          <Clause n="6" title="Confidentiality">
            Each party shall keep confidential all non-public business, technical, and pricing information disclosed by the other. This obligation survives termination for three (3) years.
          </Clause>

          <Clause n="7" title="Term &amp; Termination">
            This Agreement begins on the date signed and continues through final delivery and acceptance of the platform. Either party may terminate for material breach with thirty (30) days written notice if the breach is not cured. Termination by Client after work has commenced does not relieve Client of payment for work performed and authorized through the termination date.
          </Clause>

          <Clause n="8" title="Limitation of Liability">
            Neither party shall be liable for indirect, incidental, or consequential damages. Render Pros&rsquo; aggregate liability shall not exceed the total fees paid by Client under this Agreement.
          </Clause>

          <Clause n="9" title="Governing Law">
            This Agreement is governed by the laws of the State of Florida, without regard to its conflict-of-laws principles. Any dispute shall be resolved in the state or federal courts located in Miami-Dade County, Florida.
          </Clause>

          <Clause n="10" title="Entire Agreement">
            This document, together with any signed change orders, constitutes the entire agreement between the parties and supersedes all prior or contemporaneous proposals, communications, and understandings.
          </Clause>
        </LESection>

        {/* SIGNATURE ────────────────────────────────────────────────────── */}
        <section className="mt-14 pt-10 border-t border-black/[0.1] page-break-inside-avoid">
          <p className="text-[11px] uppercase tracking-[0.2em] mb-8" style={{ color: CYAN }}>Acceptance &amp; Signature</p>
          <div className="grid grid-cols-2 gap-12">
            <SignBlock party="Client" name={quote.client_entity || quote.client_name} />
            <SignBlock party="The Render Pros LLC" name="Authorized Signatory" />
          </div>
          <p className="text-[11px] text-[#8A8783] mt-10 text-center">
            This proposal is valid through {validThrough}. By signing above, Client accepts the scope, fees, payment schedule, and agreement terms set forth herein.
          </p>
        </section>
      </article>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, html { background: #ffffff !important; }
          article { padding: 0.5in !important; max-width: 100% !important; }
          @page { size: letter portrait; margin: 0.5in; }
          .page-break-before { page-break-before: always; }
          .page-break-inside-avoid { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function LESection({ label, children, pageBreakBefore = false }) {
  return (
    <section className={`mb-12 ${pageBreakBefore ? "page-break-before pt-10" : ""}`}>
      <p
        className="text-[10px] uppercase tracking-[0.28em] mb-6"
        style={{ color: CYAN }}
        dangerouslySetInnerHTML={{ __html: label }}
      />
      <div>{children}</div>
    </section>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8783]">{label}</p>
      <p className="text-[13px] text-[#000000] mt-1">{value}</p>
    </div>
  );
}

function Section({ label, children, pageBreakBefore = false }) {
  return (
    <section
      className={`mb-12 ${pageBreakBefore ? "page-break-before pt-10" : ""}`}
      dangerouslySetInnerHTML={undefined}
    >
      <p
        className="text-[10px] uppercase tracking-[0.28em] mb-6"
        style={{ color: ACCENT }}
        dangerouslySetInnerHTML={{ __html: label }}
      />
      <div>{children}</div>
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex items-baseline justify-between border-b border-black/[0.05] py-1.5">
      <span className="text-[#565350]">{label}</span>
      <span className="text-[#000000] tabular-nums">{value}</span>
    </div>
  );
}

function SubHeading({ children }) {
  return (
    <p
      className="text-[11px] uppercase tracking-[0.2em] mt-6 mb-3"
      style={{ color: ACCENT }}
    >
      {children}
    </p>
  );
}

/* Mirrors _vr_tours_sub in backend/routers/vexos_proposal_pdf.py: one line
   per tour with its points. Quotes saved before the tour list existed fall
   back to the min–max range. */
function vrToursSub(count, tours, itemNoun, pmin, pmax, rangeUnit) {
  const n = Number(count) || 0;
  const head = `${n} tour${n === 1 ? "" : "s"}`;
  const list = (Array.isArray(tours) ? tours : []).filter(t => Number(t?.points) > 0);
  if (list.length) {
    const total = list.reduce((s, t) => s + Number(t.points), 0);
    if (list.length === 1) return `${head} &middot; ${total} point${total === 1 ? "" : "s"}`;
    return [`${head} &middot; ${total} points total`]
      .concat(list.map((t, i) => `${itemNoun} ${Number(t.n) || i + 1} &middot; ${Number(t.points)} point${Number(t.points) === 1 ? "" : "s"}`))
      .join("<br>");
  }
  const lo = Number(pmin) || 0, hi = Number(pmax) || 0;
  if (lo > 0) return `${head} &middot; ${lo === hi ? lo : `${lo}&ndash;${hi}`} points per ${rangeUnit}`;
  return head;
}

// ScopeLine renders label/sub as HTML (they carry &middot; entities), so any
// rep-typed text — the conceptual design fee note — goes through this first.
function escapeHtml(text) {
  return String(text ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
}

function ScopeLine({ label, value, sub, accent = false }) {
  return (
    <div className="flex items-start justify-between gap-6 py-2.5 border-b border-black/[0.05]">
      <div className="min-w-0 flex-1">
        <p
          className="text-[13px] text-[#000000]"
          dangerouslySetInnerHTML={{ __html: label }}
        />
        {sub && (
          <p
            className="text-[11px] text-[#8A8783] mt-0.5"
            dangerouslySetInnerHTML={{ __html: sub }}
          />
        )}
      </div>
      <span
        className="text-[14px] tabular-nums shrink-0"
        style={{ color: accent ? "#21593D" : "#000000", fontWeight: 450 }}
      >
        {value}
      </span>
    </div>
  );
}

/** The pay-in-full alternative to a milestone schedule. Deliberately
 *  un-numbered and set in its own bordered block: it REPLACES the payments
 *  above, and a numbered row would read as another amount owed. Renders
 *  nothing when there is no one-time money to discount. */
function PifOption({ offer, copy }) {
  if (!offer?.eligible) return null;
  return (
    <div
      className="mt-4 mb-1 rounded-lg px-4 py-3.5 flex items-start justify-between gap-6"
      style={{ background: "rgba(143,167,155,0.07)", border: `1px solid ${ACCENT}55` }}
      data-testid="proposal-pif-option"
    >
      <div>
        <p className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: ACCENT }}>Alternative to the schedule above</p>
        <p className="text-[14px] text-[#000000]">{copy.label}</p>
        <p className="text-[11px] text-[#8A8783] mt-0.5">{copy.sub}</p>
      </div>
      <div className="text-right shrink-0">
        <span className="block text-[16px] tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>{fmt(offer.total)}</span>
        {/* No discount on this quote — a "You save $0" line would read as a
            broken discount rather than as the full-price option it is. */}
        {offer.savings > 0 && (
          <span className="block text-[11px] tabular-nums mt-0.5" style={{ color: ACCENT }}>You save {fmt(offer.savings)}</span>
        )}
      </div>
    </div>
  );
}

function PaymentRow({ n, label, sub, value }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-black/[0.06]">
      <div className="flex items-baseline gap-4">
        <span
          className="text-[11px] tabular-nums"
          style={{ color: ACCENT, letterSpacing: "0.1em" }}
        >
          {n}
        </span>
        <div>
          <p className="text-[14px] text-[#000000]">{label}</p>
          <p className="text-[11px] text-[#8A8783] mt-0.5" dangerouslySetInnerHTML={{ __html: sub }} />
        </div>
      </div>
      <span
        className="text-[16px] tabular-nums"
        style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}
      >
        {value}
      </span>
    </div>
  );
}

function Clause({ n, title, children }) {
  return (
    <div className="mb-5">
      <p className="text-[13px] font-medium text-[#000000] mb-1.5">
        <span className="mr-2 text-[#8A8783]">{n}.</span>
        <span dangerouslySetInnerHTML={{ __html: title }} />
      </p>
      <p className="text-[12.5px] leading-relaxed text-[#302E2B]">{children}</p>
    </div>
  );
}

function SignBlock({ party, name }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8783] mb-3">{party}</p>
      <div className="border-b border-[#000000] h-10 mb-2" />
      <p className="text-[11px] text-[#8A8783]">Signature</p>
      <div className="grid grid-cols-2 gap-3 mt-5">
        <div>
          <div className="border-b border-[#000000] h-7 mb-2" />
          <p className="text-[10px] text-[#8A8783]">Printed name</p>
          <p className="text-[11px] text-[#302E2B] mt-0.5">{name}</p>
        </div>
        <div>
          <div className="border-b border-[#000000] h-7 mb-2" />
          <p className="text-[10px] text-[#8A8783]">Date</p>
        </div>
      </div>
    </div>
  );
}
